import type { Metadata } from 'next';

import { ImageUrl } from '@/components/Utility/StrapiImage';
import { DEFAULT_METADATA } from '@/defaultMetadata';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getOrgConfig } from '@/lib/helpers/orgConfig';
import { getResourceCenterContent } from '@/lib/queries/Resources';
import { getDomain, removeTrailingBackslash } from '@/lib/UrlHelpers';
import { SiteSetting, UploadFile } from '@/types';

/**
 * Check if static generation should be skipped during Docker builds.
 * Pages will be generated via revalidation after deployment.
 */
export function shouldSkipStaticGeneration(): boolean {
  return (
    process.env.SKIP_STATIC_GENERATION === 'true' ||
    (!process.env.STRAPI_URL && !process.env.NEXT_PUBLIC_STRAPI_URL)
  );
}

/**
 * Generic helper for generateStaticParams that handles the common pattern
 * of skipping static generation during Docker builds.
 *
 * @param generatorFn Function that generates the static params
 * @param logMessage Message to log when skipping static generation
 * @returns Array of static params or empty array if skipped
 */
export async function generateStaticParamsHelper<T>(
  generatorFn: () => Promise<T[]>,
  logMessage: string
): Promise<T[]> {
  if (shouldSkipStaticGeneration()) {
    // eslint-disable-next-line no-console
    console.log(`⏭️  ${logMessage}`);
    return [];
  }

  try {
    return await generatorFn();
  } catch (error) {
    // If Strapi is unavailable, return empty array
    // Pages will be generated via revalidation after deployment
    console.warn(
      `⚠️ generateStaticParams failed, skipping static generation (will use revalidation):`,
      error instanceof Error ? error.message : 'Unknown error'
    );
    return [];
  }
}

/**
 * Check if resource center is available (acts as feature flag).
 * This function is used across multiple resource pages.
 */
export async function isResourceCenterAvailable(draftModeEnabled: boolean): Promise<boolean> {
  try {
    const { data } = await getResourceCenterContent(draftModeEnabled);
    return !!data?.resourceCenter;
  } catch (error) {
    console.error('Error checking resource center availability:', error);
    return false;
  }
}

/**
 * Build metadata object from SEO data with common patterns.
 */
export interface SEOData {
  metaTitle?: string | null;
  metaDescription?: string | null;
  keywords?: string | null;
  metaRobots?: string | null;
  metaImage?: UploadFile | null;
  openGraph?: {
    og_title?: string | null;
    og_description?: string | null;
    og_image?: UploadFile | null;
    og_url?: string | null;
    og_type?: string | null;
  } | null;
  canonicalURL?: string | null;
}

/**
 * Resolve a Strapi UploadFile to a full absolute URL string, or return null.
 */
function resolveImageUrl(image?: UploadFile | null): string | null {
  return getImageUrl({ image: image ?? null, size: 'large' });
}

/**
 * Generate metadata from SEO data with fallbacks.
 * Pass siteSetting to enable the global MetadataDefaultOgImage fallback image.
 */
export function generateMetadataFromSEO(
  seo: SEOData | null | undefined,
  fallbackTitle?: string | null,
  fallbackDescription?: string | null,
  siteSetting?: SiteSetting | null,
  resolvedCanonicalUrl?: string | null
): Metadata {
  const org = getOrgConfig();

  // Image priority: og_image → metaImage → site-wide DefaultOgImage
  const ogImage =
    resolveImageUrl(seo?.openGraph?.og_image) ??
    resolveImageUrl(seo?.metaImage) ??
    resolveImageUrl(siteSetting?.MetadataDefaultOgImage);

  const ogType = seo?.openGraph?.og_type || 'website';

  // Description priority: CMS og_description → CMS metaDescription → caller fallback → org description
  const description = seo?.metaDescription || fallbackDescription || org.description || undefined;
  const ogDescription =
    seo?.openGraph?.og_description ||
    seo?.metaDescription ||
    fallbackDescription ||
    org.description ||
    undefined;

  return {
    title: seo?.metaTitle || fallbackTitle || undefined,
    description,
    keywords: seo?.keywords || undefined,
    robots: seo?.metaRobots ?? 'index, follow',
    icons: (() => {
      const metaImage = seo?.metaImage;
      if (!metaImage) return undefined;
      const iconUrl = ImageUrl({ image: metaImage, size: 'small' });
      return iconUrl ? [new URL(iconUrl)] : undefined;
    })(),
    openGraph: {
      siteName: DEFAULT_METADATA.siteName,
      type: ogType as 'website' | 'article',
      title: seo?.openGraph?.og_title || seo?.metaTitle || fallbackTitle || undefined,
      description: ogDescription,
      url:
        seo?.openGraph?.og_url ||
        seo?.canonicalURL ||
        resolvedCanonicalUrl ||
        removeTrailingBackslash(getDomain()),
      ...(ogImage ? { images: [{ url: ogImage, width: 1200, height: 630 }] } : {}),
    },
    twitter: {
      card: ogImage ? 'summary_large_image' : 'summary',
      title: seo?.openGraph?.og_title || seo?.metaTitle || fallbackTitle || undefined,
      description: ogDescription,
      ...(ogImage ? { images: [ogImage] } : {}),
    },
    // Canonical is injected by CanonicalLink in (main) layout (no trailing slash).
  };
}

/**
 * Generate metadata with NoIndex support.
 * If NoIndex is true, sets robots to 'noindex, nofollow'.
 */
export function generateMetadataWithNoIndex(
  seo: SEOData | null | undefined,
  noIndex: boolean | null | undefined,
  fallbackTitle?: string | null,
  fallbackDescription?: string | null,
  siteSetting?: SiteSetting | null,
  resolvedCanonicalUrl?: string | null
): Metadata {
  const metadata = generateMetadataFromSEO(
    seo,
    fallbackTitle,
    fallbackDescription,
    siteSetting,
    resolvedCanonicalUrl
  );

  if (noIndex) {
    metadata.robots = 'noindex, nofollow';
  }

  return metadata;
}

export const getPageCodeFromPath = (currentPageSlug: string): string => {
  const slug = currentPageSlug.startsWith('/') ? currentPageSlug.substring(1) : currentPageSlug;
  return slug ? slug.replace(/\//g, '_') : 'home';
};
